import React, { useCallback, useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import RelevanceEditor from './RelevanceEditor';

const EMPTY_REL = { sector_codes: [], sub_industry_codes: [], tickers: [] };
const relIsEmpty = (r) => !r.sector_codes.length && !r.sub_industry_codes.length && !r.tickers.length;

// Superuser-only: trigger a channel backfill + watch runs (new / skipped / failed).
const GOLD = 'rgb(var(--brand-gold-rgb))';

function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

const statusPill = (st) => ({
  queued: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  running: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  stopped: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
}[st] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300');

// Admin strip: the team's pending backfill requests (request→approve workflow). Approve
// dispatches the real backfill (the Run appears in Recent runs below and is linked back to
// the request so the requester tracks progress); Reject records who/when/why.
function PendingRequestsStrip({ onDecided }) {
  const [reqs, setReqs] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const all = (await researchosService.getBackfillRequests()).data || [];
      setReqs(all.filter((r) => r.status === 'pending'));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (r, action) => {
    const reason = action === 'reject'
      ? (window.prompt('Reason for rejection (shown to the requester):') || '').trim()
      : undefined;
    if (action === 'reject' && !reason) return;   // cancelled prompt = no decision
    setBusyId(r.id);
    try {
      await researchosService.decideBackfillRequest(r.id, action, reason);
      await load();
      onDecided?.();
    } catch { /* surface stays; next load shows truth */ } finally { setBusyId(null); }
  };

  if (!reqs.length) return null;
  return (
    <div className="mb-5 rounded-lg border p-4" style={{ borderColor: GOLD }}>
      <div className="mb-2 text-[11px] uppercase tracking-wider" style={{ color: GOLD }}>
        Pending backfill requests ({reqs.length})
      </div>
      {reqs.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-100 py-2 text-sm last:border-b-0 dark:border-gray-800/60">
          <span className="font-medium text-gray-900 dark:text-gray-100">{r.name || r.channel_url}</span>
          <span className="text-gray-500">by {r.requested_by}</span>
          <span className="text-gray-500">win {r.years === 'all' ? 'all' : `${r.years}y`}</span>
          {r.min_duration != null && <span className="text-gray-500">≥{r.min_duration}m</span>}
          {r.note && <span className="text-xs italic text-gray-400">“{r.note}”</span>}
          <span className="ml-auto flex gap-2">
            <button disabled={busyId === r.id} onClick={() => decide(r, 'approve')}
              className="rounded border border-green-500 px-2 py-0.5 text-xs text-green-600 disabled:opacity-50 dark:text-green-400">Approve</button>
            <button disabled={busyId === r.id} onClick={() => decide(r, 'reject')}
              className="rounded border border-red-400 px-2 py-0.5 text-xs text-red-500 disabled:opacity-50">Reject</button>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function BackfillPanel() {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [years, setYears] = useState('1');
  const [minDur, setMinDur] = useState('');   // minutes; blank = no duration filter
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [rel, setRel] = useState(EMPTY_REL);
  const [showRel, setShowRel] = useState(false);
  const [runs, setRuns] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [usage, setUsage] = useState(null);

  const loadRuns = useCallback(async () => {
    try { setRuns((await researchosService.getBackfillRuns()).data || []); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  // poll while any run is active
  useEffect(() => {
    const active = runs.some((r) => r.status === 'running' || r.status === 'queued');
    if (!active) return undefined;
    const t = setInterval(loadRuns, 8000);
    return () => clearInterval(t);
  }, [runs, loadRuns]);

  useEffect(() => {
    researchosService.getWebshareUsage()
      .then((r) => setUsage(r.data)).catch((e) => setUsage({ error: e?.response?.data?.error || 'unavailable' }));
  }, []);

  // Pre-backfill PREVIEW (2026-09-25): one read-only enumeration showing exactly what this
  // channel+window+duration-floor WOULD fetch, so a 3k-video channel is a decision, not a
  // surprise. Start is a second, explicit click.
  const [preview, setPreview] = useState(null);   // null | 'loading' | coverage dict | {error}
  const runPreview = async () => {
    if (!url.trim()) { setMsg('Channel URL required.'); return; }
    setPreview('loading'); setMsg('');
    try {
      const r = await researchosService.getChannelCoverage({
        channel_url: url.trim(), years,
        min_duration: minDur ? parseInt(minDur, 10) : undefined,
      });
      setPreview(r.data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.error || 'preview failed (proxy/network?)' });
    }
  };

  const stopRun = async (r) => {
    if (!window.confirm(`Stop the ${r.stats?.channel || ''} backfill? Everything fetched so far is kept — resume anytime with ↻ retry (adjust criteria first if you want).`)) return;
    try { await researchosService.stopBackfillRun(r.id); } catch { /* list refresh shows truth */ }
    loadRuns();
  };

  const submit = async () => {
    if (!url.trim()) { setMsg('Channel URL required.'); return; }
    setBusy(true); setMsg('');
    try {
      await researchosService.triggerBackfill({ channel_url: url.trim(), name: name.trim() || undefined, years, min_duration: minDur ? parseInt(minDur, 10) : undefined });
      setPreview(null);
      // Optional: persist user-declared relevance for this channel (resolves channel_id server-side).
      if (!relIsEmpty(rel)) {
        try {
          await researchosService.saveChannelRelevance({ channel_url: url.trim(), display_name: name.trim() || undefined, ...rel });
        } catch { /* relevance is best-effort; the backfill already started */ }
      }
      setMsg('Backfill started — watch the run below.');
      setUrl(''); setName(''); setRel(EMPTY_REL); setShowRel(false);
      setTimeout(loadRuns, 1500);
    } catch (e) {
      setMsg('Failed to start: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  // Retry = pre-fill the form from a finished run and let the user adjust (e.g. the new
  // min-duration) before resubmitting. The backfill itself is resume-safe: enumerate →
  // skip-existing → fetch only what's missing, so a retry never re-downloads or re-extracts.
  const retryFrom = (r) => {
    const s = r.stats || {};
    setUrl(s.channel_url || '');
    setName(s.channel && s.channel !== s.channel_url ? s.channel : '');
    setYears(['1', '2', '3'].includes(String(s.years)) ? String(s.years) : 'all');
    setMinDur(s.min_duration != null ? String(s.min_duration) : '');
    setMsg(`Form pre-filled from run #${r.id} — adjust (e.g. min duration) and hit Backfill. `
      + 'Already-fetched videos are skipped automatically, so this resumes where it stopped.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const gb = usage && usage.bandwidth_used_gb != null ? usage.bandwidth_used_gb
    : (usage && usage.bandwidth_used != null ? (usage.bandwidth_used / 1e9).toFixed(3) : null);
  const gbLimit = usage && (usage.bandwidth_limit_gb ?? (usage.bandwidth_limit ? (usage.bandwidth_limit / 1e9).toFixed(1) : null));

  return (
    <div>
      <PendingRequestsStrip onDecided={loadRuns} />
      {/* Backfill form */}
      <div className="mb-5 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-3 text-[11px] uppercase tracking-wider text-gray-500">Backfill a channel</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <label className="text-xs text-gray-500">Channel URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/@allin"
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-44">
            <label className="text-xs text-gray-500">Display name (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="All-In Podcast"
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500">Window</label>
            <select value={years} onChange={(e) => setYears(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900">
              <option value="1">1 year</option>
              <option value="2">2 years</option>
              <option value="3">3 years</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Min duration (min)</label>
            <input type="number" min="0" value={minDur} onChange={(e) => setMinDur(e.target.value)} placeholder="e.g. 10"
              className="w-28 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900" />
          </div>
          <button onClick={runPreview} disabled={busy || preview === 'loading'}
            className="rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
            {preview === 'loading' ? 'Enumerating…' : 'Preview'}
          </button>
        </div>
        {preview && preview !== 'loading' && (
          preview.error ? (
            <p className="mt-2 text-xs text-red-500">{preview.error}</p>
          ) : (
            <div className="mt-3 rounded border p-3 text-sm" style={{ borderColor: GOLD }}>
              <span className="tabular-nums text-gray-700 dark:text-gray-200">
                {preview.enumerated_total} videos on channel · {preview.in_window_stored} already stored
                {preview.too_short_missing > 0 && ` · ${preview.too_short_missing} under the duration floor`}
                {preview.older_than_window > 0 && ` · ${preview.older_than_window} older than window`}
                {' → '}
                <b style={{ color: GOLD }}>{preview.in_window_missing} would be fetched</b>
                {' '}(then extracted by the daily chain)
              </span>
              <span className="ml-3 inline-flex gap-2">
                <button onClick={submit} disabled={busy}
                  className="rounded px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
                  style={{ background: GOLD }}>
                  {busy ? 'Starting…' : `Start backfill (${preview.in_window_missing})`}
                </button>
                <button onClick={() => setPreview(null)}
                  className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-500 dark:border-gray-700">Cancel</button>
              </span>
            </div>
          )
        )}
        {/* Optional relevance — which sectors / sub-industries / tickers this channel speaks to.
            Stored on the channel; LLM extraction can add more later (never overwriting this). */}
        <div className="mt-3">
          <button onClick={() => setShowRel((v) => !v)} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            {showRel ? '▾' : '▸'} Relevance (optional)
            {!showRel && !relIsEmpty(rel) && <span className="ml-1" style={{ color: GOLD }}>· set</span>}
          </button>
          {showRel && (
            <div className="mt-2 rounded border border-gray-200 p-3 dark:border-gray-800">
              <RelevanceEditor value={rel} onChange={setRel} />
            </div>
          )}
        </div>
        {msg && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{msg}</p>}
      </div>

      {/* Webshare usage */}
      <div className="mb-5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span className="uppercase tracking-wider">Webshare bandwidth:</span>
        {usage == null ? <span>loading…</span>
          : usage.error ? <span className="text-gray-400">{usage.error}</span>
          : <span className="tabular-nums text-gray-700 dark:text-gray-300">{gb ?? '—'} GB used{gbLimit ? ` / ${gbLimit} GB` : ''}</span>}
      </div>

      {/* Runs */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800">
        <div className="border-b border-gray-100 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 dark:border-gray-800/60">
          Recent runs
        </div>
        {runs.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">No runs yet.</p>
        ) : runs.map((r) => {
          const s = r.stats || {};
          const isOpen = openRun === r.id;
          const fails = s.failures || [];
          const done = (s.new || 0) + (s.had || 0) + (s.failed || 0) + (s.out_of_window || 0);
          const total = s.enumerated || 0;
          const pct = total ? Math.min(100, Math.round((100 * done) / total)) : 0;
          return (
            <div key={r.id} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800/60">
              <button onClick={() => setOpenRun(isOpen ? null : r.id)}
                className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800/40">
                <span className={`rounded px-1.5 py-0.5 text-[10px] ${statusPill(r.status)}`}>{r.status}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{s.channel || s.channel_url}</span>
                <span className="text-gray-500">win {s.since || (s.years ? `${s.years}y` : 'all')}</span>
                {total > 0 && <span className="tabular-nums text-gray-600 dark:text-gray-300">{total} found</span>}
                <span className="tabular-nums text-green-600 dark:text-green-400">{s.new ?? 0} new</span>
                <span className="tabular-nums text-gray-500">{s.had ?? 0} had</span>
                <span className={`tabular-nums ${(s.failed ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{s.failed ?? 0} failed</span>
                <span className="ml-auto flex items-center gap-3">
                  {(r.status === 'running' || r.status === 'queued') && (
                    <span role="button" tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); stopRun(r); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); stopRun(r); } }}
                      className="text-xs text-red-500 hover:underline">⏹ stop</span>
                  )}
                  {(r.status === 'failed' || r.status === 'success' || r.status === 'stopped') && s.channel_url && (
                    <span role="button" tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); retryFrom(r); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); retryFrom(r); } }}
                      className="text-xs hover:underline" style={{ color: GOLD }}>↻ retry</span>
                  )}
                  <span className="text-xs text-gray-400">{fmtTime(r.started_at)}</span>
                </span>
              </button>
              {r.status === 'running' && total > 0 && (
                // Live: bar tracks how far we've walked the enumerated list toward the window boundary.
                <div className="px-3 pb-2">
                  <div className="h-1.5 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
                    <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: GOLD }} />
                  </div>
                  <div className="mt-0.5 text-[10px] tabular-nums text-gray-400">{done}/{total} walked · {pct}%</div>
                </div>
              )}
              {r.status !== 'running' && total > 0 && (
                // Finished: no misleading %, since a windowed run stops early at the age cutoff and
                // never processes the older tail. Show the fetched-vs-total breakdown explicitly.
                <div className="px-3 pb-2 text-[10px] tabular-nums text-gray-400">
                  <span className="text-green-600 dark:text-green-400">{s.new ?? 0} fetched</span>
                  {' of '}{total} enumerated · {s.had ?? 0} already had · {s.failed ?? 0} failed
                  {(s.out_of_window ?? 0) > 0 && ` · ${s.out_of_window} older than window`}
                </div>
              )}
              {isOpen && fails.length > 0 && (
                <div className="bg-gray-50 px-4 py-2 dark:bg-gray-800/30">
                  <div className="mb-1 text-[11px] uppercase tracking-wider text-gray-500">Failures ({fails.length})</div>
                  <ul className="space-y-0.5">
                    {fails.map((f, i) => (
                      <li key={i} className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-mono text-gray-500">{f.id}</span> {f.title} — <span className="text-red-500">{f.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
